import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateZakazka } from "@/api/hooks/useZakazky";
import { api, extractErrorMessage } from "@/api/client";
import {
  PripojitDialog,
  type ZakazkaNaPripojenie,
} from "@/components/zakazka/PripojitDialog";
import { toast } from "@/components/ui/use-toast";
import { jeCisloAa } from "@/lib/druhCisla";
import type { ListResponse, ZakazkaDetail, ZakazkaListItem } from "@/api/schemas";

/**
 * Doplniť číslo AA výpočtu (KOLO 200). Zo zadania: *„Pri rozpočte bez čísla
 * tlačidlo Doplniť číslo zákazky. Keď číslo už má iná zákazka, nič
 * neprepíš."* AA sa stane hlavným číslom, V ostane druhým ako pri výrobnej
 * variante; Tandem Zákazka AA dostane pri ďalšom odoslaní výkresu.
 */
export function DoplnitAaDialog({
  zakazka,
  open,
  onOpenChange,
}: {
  zakazka: ZakazkaDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const update = useUpdateZakazka();
  const [cislo, setCislo] = useState("");
  const [chyba, setChyba] = useState<string | null>(null);
  // Číslo má zákazka s výrobou z importu — tú sa dá k výpočtu pripojiť
  // namiesto doplnenia (KOLO 208)
  const [vyroba, setVyroba] = useState<ZakazkaNaPripojenie | null>(null);
  const [pripojit, setPripojit] = useState(false);

  useEffect(() => {
    if (!open) {
      setCislo("");
      setChyba(null);
      setVyroba(null);
    }
  }, [open]);

  async function najdiVyrobu(c: string): Promise<void> {
    try {
      const { data } = await api.get<ListResponse<ZakazkaListItem>>("/zakazky", {
        params: { druh: "vyroba", search: c, per_page: 5 },
      });
      const z = data.data.find((x) => x.cislo_zakazky.toUpperCase() === c);
      setVyroba(z ? { id: z.id, cislo_zakazky: z.cislo_zakazky, nazov: z.nazov } : null);
    } catch {
      setVyroba(null);
    }
  }

  const text = cislo.trim().toUpperCase();
  const zlyTvar = text !== "" && !jeCisloAa(text);

  async function uloz(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!text || zlyTvar) return;
    try {
      await update.mutateAsync({ id: zakazka.id, data: { cislo_zakazky: text } });
      toast({
        title: "Číslo AA doplnené",
        description: `${zakazka.cislo_zakazky} je teraz zákazka ${text}.`,
      });
      onOpenChange(false);
    } catch (err) {
      // Obsadené číslo — server nič nezmenil, veta ostane v okne
      setChyba(extractErrorMessage(err));
      void najdiVyrobu(text);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Doplniť číslo AA</DialogTitle>
          <DialogDescription>
            Z výpočtu {zakazka.cislo_zakazky} bude zákazka. Číslo{" "}
            {zakazka.cislo_zakazky} ostane ako číslo výpočtu.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void uloz(e)} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="doplnit-aa">Číslo zákazky</Label>
            <Input
              id="doplnit-aa"
              autoFocus
              value={cislo}
              placeholder="napr. AA6319"
              aria-invalid={zlyTvar || Boolean(chyba)}
              onChange={(e) => {
                setCislo(e.target.value);
                setChyba(null);
              }}
            />
            {zlyTvar ? (
              <p className="text-xs text-destructive">
                Číslo zákazky má tvar písmená a číslice, napr. AA6319. Čísla V a Z
                prideľuje Rozpočet.
              </p>
            ) : null}
            {chyba ? (
              <p role="alert" className="text-xs text-destructive">
                {chyba}
              </p>
            ) : null}
            {vyroba ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                {vyroba.cislo_zakazky} je výroba z TopSolidu. Pripoj ju k tomuto výpočtu —
                zákazka dostane číslo {vyroba.cislo_zakazky} a výpočty ostanú.
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2 h-7 w-full text-xs"
                  onClick={() => setPripojit(true)}
                >
                  Pripojiť výrobu {vyroba.cislo_zakazky}
                </Button>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Zrušiť
            </Button>
            <Button type="submit" disabled={!text || zlyTvar || update.isPending}>
              {update.isPending ? "Ukladá sa…" : "Doplniť"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <PripojitDialog
        zakazka={pripojit ? zakazka : null}
        strana="vypocet"
        predvolena={vyroba}
        onOpenChange={setPripojit}
        onPripojene={() => onOpenChange(false)}
      />
    </Dialog>
  );
}
